document.addEventListener('DOMContentLoaded', () => {
  initHeaderControls(document);
  initProductForms(document);
  initProductMediaControls(document);
  initCollectionShowcases(document);
  initCarousels(document);
});

function initHeaderControls(scope = document) {
  const mobileToggle = scope.querySelector('[data-mobile-toggle]');
  const mobilePanel = document.querySelector('[data-mobile-panel]');
  if (mobileToggle && mobilePanel && !mobileToggle.dataset.ready) {
    mobileToggle.dataset.ready = 'true';
    mobileToggle.addEventListener('click', () => {
      const open = mobilePanel.classList.toggle('is-open');
      mobileToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  const searchToggle = scope.querySelector('[data-search-toggle]');
  const searchDrawer = document.querySelector('[data-search-drawer]');
  if (searchToggle && searchDrawer && !searchToggle.dataset.ready) {
    searchToggle.dataset.ready = 'true';
    searchToggle.addEventListener('click', () => {
      const open = searchDrawer.classList.toggle('is-open');
      searchToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) searchDrawer.querySelector('input')?.focus();
    });
  }
}

function initProductForms(scope = document) {
  scope.querySelectorAll('[data-product-select]').forEach((select) => {
    if (select.dataset.ready) return;
    select.dataset.ready = 'true';
    select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      const root = select.closest('[data-product-root]');
      if (!root) return;
      const price = root.querySelector('[data-product-price]');
      const compare = root.querySelector('[data-product-compare]');
      const button = root.querySelector('[data-add-button]');
      if (price && option.dataset.price) price.textContent = option.dataset.price;
      if (compare) {
        compare.textContent = option.dataset.compare || '';
        compare.style.display = option.dataset.compare ? '' : 'none';
      }
      if (button) {
        const available = option.dataset.available === 'true';
        button.disabled = !available;
        button.textContent = available ? button.dataset.availableText : button.dataset.soldText;
      }
    });
  });

  scope.querySelectorAll('[data-filter-form] input, [data-filter-form] select').forEach((el) => {
    if (el.matches('[data-auto-submit]') && !el.dataset.ready) {
      el.dataset.ready = 'true';
      el.addEventListener('change', () => el.form?.submit());
    }
  });
}

function formatMediaTime(value) {
  if (!Number.isFinite(value) || value < 0) return '0:00';
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function initProductMediaControls(scope = document) {
  const shells = [];
  if (scope instanceof Element && scope.matches('[data-product-video-shell]')) shells.push(scope);
  if (scope.querySelectorAll) shells.push(...scope.querySelectorAll('[data-product-video-shell]'));

  shells.forEach((shell) => {
    if (shell.dataset.videoReady === 'true') return;
    const video = shell.querySelector('video');
    if (!video) return;

    shell.dataset.videoReady = 'true';
    const button = shell.querySelector('[data-product-video-toggle]');
    const soundButton = shell.querySelector('[data-product-video-sound]');
    const progress = shell.querySelector('[data-product-video-progress]');
    const time = shell.querySelector('[data-product-video-time]');
    const controls = shell.querySelector('[data-product-video-controls]');

    const syncButton = () => {
      if (!button) return;
      const playing = !video.paused && !video.ended;
      button.classList.toggle('is-playing', playing);
      button.setAttribute('aria-pressed', playing ? 'true' : 'false');
      button.setAttribute('aria-label', playing ? 'Pausar video' : 'Reproducir video');
    };

    const syncSound = () => {
      if (!soundButton) return;
      const muted = video.muted || video.volume === 0;
      soundButton.classList.toggle('is-muted', muted);
      soundButton.setAttribute('aria-pressed', muted ? 'true' : 'false');
      soundButton.setAttribute('aria-label', muted ? 'Activar sonido' : 'Silenciar video');
    };

    const syncProgress = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const current = Number.isFinite(video.currentTime) ? video.currentTime : 0;
      const ratio = duration > 0 ? Math.max(0, Math.min(1, current / duration)) : 0;
      if (progress) {
        progress.value = String(Math.round(ratio * 1000));
        progress.style.setProperty('--video-progress', `${ratio * 100}%`);
        progress.disabled = duration <= 0;
      }
      if (time) time.textContent = `${formatMediaTime(current)} / ${formatMediaTime(duration)}`;
    };

    // Controls consume their own gestures so dragging the timeline never changes slides.
    // The rest of the video surface stays swipeable because native controls are disabled.
    if (controls) {
      ['pointerdown', 'pointerup', 'pointercancel', 'click'].forEach((type) => {
        controls.addEventListener(type, (event) => event.stopPropagation());
      });
    }

    if (button) {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        if (video.paused || video.ended) {
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    if (soundButton) {
      soundButton.addEventListener('click', (event) => {
        event.preventDefault();
        video.muted = !video.muted;
        if (!video.muted && video.volume === 0) video.volume = 1;
        syncSound();
      });
    }

    if (progress) {
      const seek = () => {
        if (!Number.isFinite(video.duration) || video.duration <= 0) return;
        video.currentTime = (Number(progress.value) / 1000) * video.duration;
        syncProgress();
      };
      progress.addEventListener('input', seek);
      progress.addEventListener('change', seek);
    }

    ['loadedmetadata', 'durationchange', 'timeupdate', 'seeking', 'seeked'].forEach((type) => video.addEventListener(type, syncProgress));
    video.addEventListener('play', syncButton);
    video.addEventListener('pause', syncButton);
    video.addEventListener('ended', syncButton);
    video.addEventListener('volumechange', syncSound);
    syncButton();
    syncSound();
    syncProgress();
  });
}

function initCollectionShowcases(scope = document) {
  const showcases = [];
  if (scope instanceof Element && scope.matches('[data-collection-showcase]')) showcases.push(scope);
  if (scope.querySelectorAll) showcases.push(...scope.querySelectorAll('[data-collection-showcase]'));

  showcases.forEach((showcase) => {
    if (showcase.dataset.tabsReady === 'true') return;
    const tabs = Array.from(showcase.querySelectorAll('[data-collection-tab]'));
    const panels = Array.from(showcase.querySelectorAll('[data-collection-panel]'));
    if (!tabs.length || !panels.length) return;

    showcase.dataset.tabsReady = 'true';
    const selectTab = (index, focus = false) => {
      const safeIndex = Math.max(0, Math.min(index, tabs.length - 1));
      tabs.forEach((tab, i) => {
        const active = i === safeIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus({ preventScroll: true });
      });
      panels.forEach((panel, i) => {
        panel.hidden = i !== safeIndex;
      });

      // Every category starts from its first REAL product when selected.
      // This prevents a previously selected slide index from pointing at an
      // empty/non-existent position after switching collections.
      const activePanel = panels[safeIndex];
      const activeCarousel = activePanel?.querySelector('[data-mobile-carousel]');
      if (activeCarousel) {
        activeCarousel.dispatchEvent(new CustomEvent('vcici:carousel-reset'));
      }

      // Keep the selected category visible/near the center of the horizontal text rail.
      const activeTab = tabs[safeIndex];
      const tabRail = activeTab && activeTab.closest('.collection-showcase__tabs');
      if (activeTab && tabRail && tabRail.scrollWidth > tabRail.clientWidth + 2) {
        const targetLeft = activeTab.offsetLeft - (tabRail.clientWidth - activeTab.offsetWidth) / 2;
        tabRail.scrollTo({ left: Math.max(0, targetLeft), behavior: focus ? 'auto' : 'smooth' });
      }
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => selectTab(index));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        selectTab(next, true);
      });
    });

    selectTab(0);
  });
}

/*
 * VCICI v2.2 carousel system
 * - No horizontally scrollable "long strip".
 * - One isolated slide occupies the viewport at a time.
 * - Vertical page scrolling remains native on touch screens.
 * - A clearly horizontal swipe changes slides; dots are also clickable.
 */
function initCarousels(scope = document) {
  const mobileQuery = window.matchMedia('(max-width: 780px)');
  const carousels = [];
  if (scope instanceof Element && scope.matches('[data-mobile-carousel]')) carousels.push(scope);
  if (scope.querySelectorAll) carousels.push(...scope.querySelectorAll('[data-mobile-carousel]'));

  carousels.forEach((carousel) => {
    if (carousel.dataset.carouselReady === 'true') return;

    const track = carousel.querySelector('[data-mobile-carousel-track]');
    const dotsRoot = carousel.querySelector('[data-mobile-carousel-dots]');
    const controls = carousel.querySelector('[data-mobile-carousel-controls]');
    const always = carousel.hasAttribute('data-carousel-always');
    if (!track) return;

    carousel.dataset.carouselReady = 'true';
    carousel.classList.add('is-carousel-ready');

    let current = 0;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let pointerId = null;
    let suppressClickUntil = 0;

    // Only count REAL/renderable slides.
    // In product carousels this deliberately excludes empty collection messages,
    // placeholder/invalid cards and any non-product helper elements.
    const isRealSlide = (el) => {
      if (!(el instanceof HTMLElement) || el.hidden) return false;
      if (el.matches('.collection-showcase__empty')) return false;

      if (el.matches('.product-card')) {
        const productLink = el.querySelector('.product-card__media[href], .product-card__title a[href]');
        const productVisual = el.querySelector('img, svg, .placeholder-svg');
        return Boolean(productLink && productVisual);
      }

      // Known non-product carousel slide types.
      if (el.matches('.product-gallery__item, .trust-item, .category-card, .guide-card, .testimonial, .step, .comparison-rating-card')) {
        return true;
      }

      // Generic fallback: only keep an element that actually contains visible content.
      return Boolean(el.querySelector('img, video, iframe, model-viewer, svg, a, button') || el.textContent.trim());
    };

    const getItems = () => Array.from(track.children).filter(isRealSlide);
    const isActiveMode = () => always || mobileQuery.matches;

    const buildDots = () => {
      if (!dotsRoot) return;
      const items = getItems();
      dotsRoot.replaceChildren();
      items.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'mobile-carousel__dot';
        dot.setAttribute('aria-label', `Ir a la diapositiva ${index + 1}`);
        dot.addEventListener('click', () => goTo(index));
        dotsRoot.appendChild(dot);
      });
    };

    const update = () => {
      const items = getItems();
      const total = items.length;

      if (!total) {
        if (controls) controls.hidden = true;
        if (dotsRoot) dotsRoot.replaceChildren();
        return;
      }

      // Never allow an index beyond the actual products.
      current = Math.max(0, Math.min(current, total - 1));
      const activeMode = isActiveMode();

      if (dotsRoot && dotsRoot.children.length !== total) buildDots();
      if (controls) controls.hidden = !activeMode || total <= 1;

      // Reset ALL direct children first. Invalid/empty helpers can never become a slide.
      Array.from(track.children).forEach((item) => {
        const slideIndex = items.indexOf(item);
        const validSlide = slideIndex !== -1;
        const active = validSlide && slideIndex === current;

        item.classList.toggle('is-current', active);

        if (!validSlide) {
          item.setAttribute('aria-hidden', 'true');
          item.setAttribute('inert', '');
          return;
        }

        if (activeMode) {
          item.setAttribute('aria-hidden', active ? 'false' : 'true');
          if (active) item.removeAttribute('inert');
          else item.setAttribute('inert', '');
        } else {
          item.removeAttribute('aria-hidden');
          item.removeAttribute('inert');
        }

        item.querySelectorAll('video').forEach((video) => {
          if (activeMode && !active && !video.paused) video.pause();
        });
      });

      if (dotsRoot) {
        Array.from(dotsRoot.children).forEach((dot, index) => {
          const active = index === current;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }
    };

    // Finite carousel: the first/last item is the boundary.
    // No modulo wrapping, so four products can only ever produce four positions.
    const goTo = (index) => {
      const total = getItems().length;
      if (!total) return;
      current = Math.max(0, Math.min(index, total - 1));
      update();
    };

    const startPointer = (event) => {
      if (!isActiveMode() || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (event.target instanceof Element && event.target.closest('[data-no-carousel-swipe]')) return;
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
    };

    const endPointer = (event) => {
      if (pointerId === null || event.pointerId !== pointerId || !isActiveMode()) return;
      const dx = event.clientX - pointerStartX;
      const dy = event.clientY - pointerStartY;
      pointerId = null;

      // Only an intentional horizontal gesture changes the slide.
      // Vertical gestures remain normal page scrolling.
      if (Math.abs(dx) < 42 || Math.abs(dx) <= Math.abs(dy) * 1.2) return;

      const total = getItems().length;
      if (!total) return;

      const nextIndex = dx < 0 ? current + 1 : current - 1;
      const boundedIndex = Math.max(0, Math.min(nextIndex, total - 1));
      if (boundedIndex === current) return;

      suppressClickUntil = Date.now() + 400;
      goTo(boundedIndex);
    };

    const cancelPointer = () => {
      pointerId = null;
    };

    track.addEventListener('pointerdown', startPointer);
    track.addEventListener('pointerup', endPointer);
    track.addEventListener('pointercancel', cancelPointer);
    track.addEventListener('click', (event) => {
      if (Date.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    track.addEventListener('keydown', (event) => {
      if (!isActiveMode()) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(current + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goTo(current - 1);
      }
    });

    // Category changes always return this carousel to its first actual product.
    carousel.addEventListener('vcici:carousel-reset', () => {
      current = 0;
      buildDots();
      update();
    });

    if (!track.hasAttribute('tabindex')) track.setAttribute('tabindex', '0');
    buildDots();
    update();

    const refresh = () => {
      current = Math.min(current, Math.max(0, getItems().length - 1));
      buildDots();
      update();
    };

    if (typeof mobileQuery.addEventListener === 'function') mobileQuery.addEventListener('change', refresh);
    else if (typeof mobileQuery.addListener === 'function') mobileQuery.addListener(refresh);
  });
}

document.addEventListener('shopify:block:select', (event) => {
  const tab = event.target instanceof Element ? event.target.closest('[data-collection-tab]') : null;
  if (tab) tab.click();
});

document.addEventListener('shopify:section:load', (event) => {
  initHeaderControls(event.target);
  initProductForms(event.target);
  initProductMediaControls(event.target);
  initCollectionShowcases(event.target);
  initCarousels(event.target);
});
